import { PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useGetCurrentUserQuery, useIsSignedIn } from '../../apiHooks/userApi';
import { useLogoutMutation } from '../../apiHooks/authApi';
import config from '../../config';
import BioLibIcon from '../../assets/logos/biolib-icon.png';
import NavbarLink from '@components/atoms/NavbarLink';
import ProfilePicture from '@components/atoms/ProfilePicture';
import { clsx } from 'clsx';

interface INavigationItem {
    name: string;
    path: string;
}

const navigation: INavigationItem[] = [
    { name: 'Docs', path: '/docs/' },
    { name: 'Explore', path: '/explore/' },
    { name: 'Results', path: '/results/' },
];

export default function NavbarMobileMenu() {
    const isSignedIn = useIsSignedIn();
    const { data: user } = useGetCurrentUserQuery();
    const [logout] = useLogoutMutation();

    return (
        <div className='lg:hidden'>
            <PopoverBackdrop
                transition={true}
                className='fixed inset-0 z-20 bg-black/25 duration-150 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in'
            />

            <PopoverPanel
                focus
                transition
                className='absolute inset-x-0 top-0 z-30 mx-auto w-full max-w-3xl origin-top transform p-2 transition duration-150 data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in'
            >
                <div className='divide-y divide-gray-200 rounded-lg bg-white shadow-lg ring-1 ring-black/5'>
                    <div className='pb-2 pt-3'>
                        <div className='flex items-center justify-between px-4'>
                            <div>
                                <img
                                    alt='Logo'
                                    draggable={false}
                                    src={config.isEnterprise ? config.enterpriseLogoPath : BioLibIcon}
                                    className={clsx(config.isEnterprise ? 'w-10' : 'w-6')}
                                />
                            </div>
                            <div className='-mr-2'>
                                <PopoverButton className='relative inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500'>
                                    <span className='absolute -inset-0.5' />
                                    <span className='sr-only'>Close menu</span>
                                    <XMarkIcon aria-hidden='true' className='size-6' />
                                </PopoverButton>
                            </div>
                        </div>
                        <div className='mt-3 space-y-1 px-2'>
                            {navigation.map((item) => (
                                <NavbarLink
                                    key={item.name}
                                    link={item.path}
                                    className='block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800 hover:no-underline'
                                >
                                    {item.name}
                                </NavbarLink>
                            ))}
                        </div>
                    </div>
                    {isSignedIn ? (
                        <div className='pb-2 pt-4'>
                            <div className='flex items-center px-5'>
                                <div className='shrink-0'>
                                    <ProfilePicture
                                        accountHandle={user?.account.handle}
                                        src={user?.account.profile_picture_200x200 || config.placeholderProfileImage}
                                        className='size-10 rounded-full'
                                    />
                                </div>
                                <div className='ml-3 min-w-0 flex-1'>
                                    <div className='truncate text-base font-medium text-gray-800'>
                                        {user?.account.display_name}
                                    </div>
                                    <div className='truncate text-sm font-medium text-gray-500'>{user?.email}</div>
                                </div>
                            </div>
                            <div className='mt-3 space-y-1 px-2'>
                                <NavbarLink
                                    link={user?.account.handle ? `/${user.account.handle}/` : '/'}
                                    className='block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800 hover:no-underline'
                                >
                                    Your Profile
                                </NavbarLink>
                                <NavbarLink
                                    link='/settings/'
                                    className='block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800 hover:no-underline'
                                >
                                    Settings
                                </NavbarLink>
                                <button
                                    onClick={() => logout()}
                                    className='block w-full text-left rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800'
                                >
                                    Sign out
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className='pb-2 pt-4'>
                            <div className='px-2'>
                                <NavbarLink
                                    link='/sign-in/'
                                    className='block rounded-md px-3 py-2 text-center text-base font-semibold text-white bg-cyan-600 hover:bg-cyan-700 hover:no-underline'
                                >
                                    Sign in
                                </NavbarLink>
                            </div>
                        </div>
                    )}
                </div>
            </PopoverPanel>
        </div>
    );
}
